import { parse } from "date-fns";
import { useCallback, useState } from "react";

interface DragState {
  left: string;
  right: string;
}

const emptyDrag: DragState = { left: "", right: "" };

/**
 * Click-and-drag horizontal zoom for time-series charts. Returns recharts mouse
 * handlers + the active drag span so a <ReferenceArea> can render the highlight.
 * When the user releases over a different `activeLabel`, calls onZoom with the
 * resolved {from,to} (always ordered, even if dragged right-to-left).
 */
export function useDateRangeZoom({
  onZoom,
}: {
  onZoom: (range: { from: Date; to: Date }) => void;
}) {
  const [drag, setDrag] = useState<DragState>(emptyDrag);

  const onMouseDown = useCallback((e: { activeLabel?: string | number }) => {
    if (e?.activeLabel) setDrag({ left: String(e.activeLabel), right: "" });
  }, []);

  const onMouseMove = useCallback((e: { activeLabel?: string | number }) => {
    setDrag((prev) => {
      if (!prev.left || !e?.activeLabel) return prev;
      return { ...prev, right: String(e.activeLabel) };
    });
  }, []);

  const onMouseUp = useCallback(() => {
    setDrag((prev) => {
      const { left, right } = prev;
      if (!left || !right || left === right) return emptyDrag;
      // `yyyy-MM-dd` string compare is chronological — safe for ISO day strings.
      const [l, r] = [left, right].sort();
      const ref = new Date();
      onZoom({
        from: parse(l, "yyyy-MM-dd", ref),
        to: parse(r, "yyyy-MM-dd", ref),
      });
      return emptyDrag;
    });
  }, [onZoom]);

  return { drag, onMouseDown, onMouseMove, onMouseUp };
}
